import { useEffect, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { AddOnLevelType, AddOnMetaType } from "lib/types/checkout";
import { classNames, truncateString } from "lib/utils/helpers";
import defaultPackageImage from "assests/images/package.png";
import { currencyFormatter } from "lib/utils/formatters";
import { Icon } from "@shopflo/ui/icon";
import { OrderSummaryConfigType } from "lib/types/merchantConfigTypes";
import { getTextColorByBrightness } from "@shopflo/ui/utils";
import { useItemCardAlign } from "lib/hooks/useItemCardAlign";
import { useLocale } from "lib/hooks/useLocale";

export const AddOnSelectItem = ({
  apiActionLoading,
  addOnData,
  variant,
  isCartItem,
  itemQuantity = 1,
  handleButtonClick,
  customisations,
  isItemPropertiesAvailable,
}: {
  addOnData: AddOnMetaType;
  variant: AddOnLevelType;
  isCartItem?: boolean;
  itemQuantity?: number;
  handleButtonClick: () => void;
  apiActionLoading?: boolean;
  customisations?: OrderSummaryConfigType;
  isItemPropertiesAvailable?: boolean;
}) => {
  const [disabled, setDisabled] = useState(false);
  const { t } = useLocale();

  useEffect(() => {
    if (!apiActionLoading) {
      setDisabled(false);
    }
  }, [apiActionLoading]);

  const handleClick = () => {
    if (disabled) {
      return;
    }
    setDisabled(true);
    handleButtonClick();
  };

  const productName = truncateString(addOnData?.product_name, 40);
  const price = addOnData?.price
    ? currencyFormatter((addOnData.price * itemQuantity).toString())
    : t("automatic_freebies_free_text");
  const isRowAligned = useItemCardAlign(Boolean(isCartItem)) === "row";

  return (
    <div className="flex flex-col gap-2 w-full ">
      <div className="flex flex-col gap-1.5 pt-1 pr-1">
        <div className="flex flex-row gap-3">
          <div className="relative flex items-center gap-3 w-full">
            <div className="relative ">
              <div
                className="h-10 w-10 rounded-lg overflow-hidden"
                style={{
                  borderColor: getTextColorByBrightness({
                    bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
                    lightOpacity: 24,
                  }),
                }}>
                <img
                  src={addOnData?.image ?? defaultPackageImage}
                  alt={"add-on-image"}
                  className="h-full w-full object-cover"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = defaultPackageImage;
                  }}
                />
              </div>
              <button
                onClick={handleClick}
                disabled={disabled}
                className={classNames(
                  "absolute -left-2 -top-[7.5px] flex items-center justify-center rounded-md p-1 bg-zinc-800 ",
                  disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
                )}
                style={{
                  backgroundColor: customisations?.textColor,
                }}>
                <Icon
                  IconComponent={Plus}
                  size="xxxxs"
                  iconProps={{
                    weight: "bold",
                    color: customisations?.backgroundColor ?? "#FFFFFF",
                  }}
                  className="text-white"
                />
              </button>
            </div>

            <div className="flex flex-row items-center gap-5 flex-1 ">
              <div className="flex flex-col gap-1 flex-1">
                <div className="flex flex-col gap-0">
                  <p
                    className="text-xs font-medium text-zinc-700 leading-[1.5em] tracking-[-0.01em]"
                    style={{
                      color: customisations?.textColor,
                    }}>
                    {productName}
                  </p>
                  <div
                    className={classNames(
                      "flex flex-row items-center gap-1.5",
                      disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
                    )}
                    onClick={handleClick}>
                    <div className="flex flex-row items-center justify-center gap-1 rounded-lg py-0.5">
                      <div className="flex flex-row items-center gap-0.5 space-x-1">
                        {!isRowAligned && !Boolean(isItemPropertiesAvailable) && (
                          <span
                            className=" text-text-xs-semibold text-zinc-800 leading-[1.57em] tracking-[-0.01em] text-center underline decoration-1 decoration-zinc-300 underline-offset-2"
                            style={{
                              color: customisations?.textColor,
                              textDecorationColor: getTextColorByBrightness({
                                bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
                              }),
                            }}>
                            Add
                          </span>
                        )}
                        <span
                          className="text-xs font-medium text-zinc-500 leading-[1.5em] tracking-[-0.01em] text-center"
                          style={{
                            color: customisations?.textColor,
                            opacity: 0.6,
                          }}>
                          {price}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {(isRowAligned || Boolean(isItemPropertiesAvailable)) && (
              <div className="ml-auto shrink-0 w-[72px] items-end flex justify-end">
                <button
                  onClick={handleClick}
                  disabled={disabled}
                  className={classNames(
                    "flex h-7 flex-row w-full items-center justify-center gap-1 rounded border-dotted border-2 border-zinc-800/20 px-2.5 hover:!bg-black/[0.04]",
                    disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                  )}
                  style={{
                    backgroundColor: customisations?.backgroundColor,
                  }}>
                  <Icon
                    IconComponent={Plus}
                    size="xxxs"
                    iconProps={{
                      weight: "bold",
                      className: "text-zinc-500",
                      color: customisations?.textColor.substring(0, 7),
                      opacity: "60%",
                    }}
                    className="text-zinc-500"
                  />
                  <span
                    className="text-xs font-medium text-coal-dark"
                    style={{
                      color: customisations?.textColor,
                    }}>
                    Add
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
